/**
 * axiosConfig.ts - Axios 인스턴스 설정
 * 
 * 모든 API 요청에 대한 공통 설정을 담당합니다.
 * - 기본 설정 (baseURL, timeout, headers)
 * - 환경별 설정
 * - 에러 타입 정의
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '../../config/api';

// API 에러 응답 타입
export interface ApiErrorResponse {
  message: string;
  detail?: string;
  code?: string;
  status?: number;
}

// 환경별 설정 타입
export interface EnvironmentConfig {
  baseURL: string;
  timeout: number;
  withCredentials: boolean;
  headers: Record<string, string>;
}

// 환경별 설정 가져오기
const getEnvironmentConfig = (): EnvironmentConfig => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isProduction = process.env.NODE_ENV === 'production';
  
  return {
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    withCredentials: true, // CORS 요청 시 쿠키 포함
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      // 개발 환경에서만 추가 헤더
      ...(isDevelopment && {
        'X-Environment': 'development',
      }),
    },
  };
};

// Axios 기본 설정
const defaultConfig: AxiosRequestConfig = {
  ...getEnvironmentConfig(),
  // 응답 검증
  validateStatus: (status) => status >= 200 && status < 300,
  // 요청 변환
  transformRequest: [
    (data, headers) => {
      // 파일 업로드가 아닌 경우 JSON 직렬화
      if (data && !(data instanceof FormData)) {
        return JSON.stringify(data);
      }
      return data;
    },
  ],
  // 응답 변환
  transformResponse: [
    (data) => {
      // JSON 파싱 시도
      try {
        return JSON.parse(data);
      } catch (e) {
        return data;
      }
    },
  ],
};

// Axios 인스턴스 생성 함수
export const createAxiosInstance = (config?: AxiosRequestConfig): AxiosInstance => {
  const instance = axios.create({
    ...defaultConfig,
    ...config,
  });

  return instance;
};

// 기본 Axios 인스턴스
export const apiClient = createAxiosInstance();

// 인증이 필요없는 API용 인스턴스
export const publicApiClient = createAxiosInstance({
  headers: {
    ...defaultConfig.headers,
    'X-Public-API': 'true',
  },
});

// 파일 업로드용 인스턴스
export const fileApiClient = createAxiosInstance({
  headers: {
    ...defaultConfig.headers,
    'Content-Type': 'multipart/form-data',
  },
  // 파일 업로드는 timeout 늘리기
  timeout: 60000, // 1분
});

// 실시간 통신용 인스턴스 (짧은 timeout)
export const realtimeApiClient = createAxiosInstance({
  timeout: 5000, // 5초
  headers: {
    ...defaultConfig.headers,
    'X-Realtime': 'true',
  },
});

// API 에러인지 확인하는 유틸리티 함수
export const isApiError = (error: unknown): error is AxiosError<ApiErrorResponse> => {
  return axios.isAxiosError(error) && error.response?.data !== undefined;
};

// API 에러 메시지 추출 유틸리티
export const getApiErrorMessage = (error: unknown): string => {
  if (isApiError(error)) {
    return error.response?.data?.message || error.response?.data?.detail || '요청 처리 중 오류가 발생했습니다.';
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return '알 수 없는 오류가 발생했습니다.';
};

// 재시도 설정
export const retryConfig = {
  retries: 3,
  retryDelay: (retryCount: number) => {
    // exponential backoff: 1초, 2초, 4초
    return Math.pow(2, retryCount - 1) * 1000;
  },
  retryCondition: (error: AxiosError) => {
    // 네트워크 오류나 5xx 서버 오류인 경우만 재시도
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  },
};